import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiTV, tokenStore, formatApiError } from "@/lib/api";
import { Tv, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Brand from "@/components/Brand";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1645811791231-279658fb46dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMHRlY2glMjBncmFkaWVudCUyMGJsdWUlMjB3aGl0ZXxlbnwwfHx8fDE3Nzg3MTQ1NjN8MA&ixlib=rb-4.1.0&q=85";

export default function TVLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("roy-mainbar");
  const [password, setPassword] = useState("bar1234");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await apiTV.post("/tv/login", { username, password });
      tokenStore.setTV(data.token);
      toast.success(`Connected · ${data.tv.location}`);
      // Request full-screen Full HD playback (must happen in the same user gesture)
      try {
        const el = document.documentElement;
        const req =
          el.requestFullscreen ||
          el.webkitRequestFullscreen ||
          el.mozRequestFullScreen ||
          el.msRequestFullscreen;
        if (req) {
          await req.call(el).catch(() => {});
        }
      } catch {
        /* fullscreen not supported - continue anyway */
      }
      navigate("/tv", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="tv-login-page">
      <img src={BG_IMAGE} alt="bg" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-blue-900/40 to-slate-900/80" />

      <div className="relative min-h-screen flex flex-col items-center justify-center p-6">
        <div className="flex flex-col items-center mb-8 cc-fade-in">
          <div className="rounded-2xl bg-white/95 px-6 py-3 shadow-lg">
            <Brand variant="dark" className="h-7" />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-blue-200 mt-3">Fire Stick · Display agent</div>
        </div>

        <form
          onSubmit={onSubmit}
          className="w-full max-w-md rounded-3xl border border-white/20 bg-white/95 backdrop-blur-2xl p-8 shadow-2xl cc-slide-up"
          data-testid="tv-login-form"
        >
          <div className="flex items-center gap-2 text-blue-600">
            <Tv className="h-5 w-5" />
            <span className="text-xs font-semibold tracking-widest uppercase">Device authentication</span>
          </div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900 mt-3">Connect this television</h1>
          <p className="text-sm text-slate-500 mt-1">
            Enter the username and password assigned to this TV from the admin dashboard.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <Label className="text-xs font-medium text-slate-600" htmlFor="tv-username">
                TV username
              </Label>
              <Input
                id="tv-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="roy-mainbar"
                className="mt-1.5 h-12 text-base"
                data-testid="tv-login-username-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600" htmlFor="tv-password">
                Password
              </Label>
              <Input
                id="tv-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className="mt-1.5 h-12 text-base"
                data-testid="tv-login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-12 bg-blue-600 hover:bg-blue-700 text-base text-white"
            data-testid="tv-login-submit-button"
          >
            {submitting ? "Connecting..." : "Connect TV"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <div className="text-xs text-slate-500 text-center mt-5">
            <Link to="/" className="hover:text-slate-900" data-testid="tv-login-back-home">
              ← Back to home
            </Link>
          </div>
        </form>

        <div className="mt-6 text-xs text-blue-100/70">Tip: try <span className="font-mono">roy-mainbar</span> / <span className="font-mono">bar1234</span></div>
      </div>
    </div>
  );
}
